import type { EmailContact } from "@productlane/api"

/**
 * Converts raw email contact fields (cc, bcc, from, to) into a consistent
 * EmailContact array.
 *
 * Handles:
 * - JSON strings that need parsing
 * - Objects with a `value` property (Zero JSON type)
 * - Arrays of EmailContact objects
 * - Single EmailContact objects
 * - undefined/null (returns empty array)
 */
export function formatEmailContacts(contacts: unknown): EmailContact[] {
	if (!contacts) {
		return []
	}

	let parsed: unknown = contacts

	if (typeof contacts === "string") {
		try {
			parsed = JSON.parse(contacts)
		} catch {
			return []
		}
	}

	if (!parsed || typeof parsed !== "object") {
		return []
	}

	if (Array.isArray(parsed)) {
		return parsed
			.map((item): unknown => {
				if (typeof item === "string") {
					try {
						return JSON.parse(item) as unknown
					} catch {
						return null
					}
				}
				return item
			})
			.filter((contact): contact is EmailContact => {
				if (!contact || typeof contact !== "object") {
					return false
				}
				const contactObj = contact as Record<string, unknown>
				return "email" in contactObj && typeof contactObj.email === "string"
			})
	}

	if ("value" in parsed && Array.isArray(parsed.value)) {
		const valueObj = parsed as { value: unknown[] }
		return valueObj.value.filter((contact): contact is EmailContact => {
			if (!contact || typeof contact !== "object") {
				return false
			}
			const contactObj = contact as Record<string, unknown>
			return "email" in contactObj && typeof contactObj.email === "string"
		})
	}

	if ("email" in parsed && typeof parsed.email === "string") {
		return [parsed as EmailContact]
	}

	return []
}
